import { useState, useEffect, useCallback } from "react";
import { supabase as supabaseClient } from "@/integrations/supabase/client";
import { toast } from "@/hooks/ui/use-toast";
import { BusinessDetails } from "@/types/index";

const placeholderBusinessDetails: BusinessDetails = {
  id: "",
  name: "Loading...",
  description: "",
  address: "",
  website: "",
  phone: "",
  created_at: "",
  user_id: "",
};

export function useBusinessDetails(selectedBusinessId: string | null) {
  const [selectedBusinessDetails, setSelectedBusinessDetails] = useState<BusinessDetails | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  const fetchBusinessDetails = useCallback(async (businessId: string) => {
    setLoadingData(true);
    
    try {
      const { data: businessData, error } = await supabaseClient
        .from("businesses")
        .select(`
          *,
          business_opening_hours (
            id,
            day_of_week,
            opening_time,
            closing_time,
            is_closed_all_day,
            is_24_hours,
            is_open,
            created_at,
            updated_at
          )
        `)
        .eq("id", businessId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          setSelectedBusinessDetails(null);
          return;
        }
        throw error;
      }

      if (!businessData) {
        setSelectedBusinessDetails(null);
        return;
      }

      const fetchedDetails: BusinessDetails = {
        id: businessData.id,
        name: businessData.name || "Unnamed Business",
        description: businessData.description || "",
        address: businessData.address || "",
        website: businessData.website || "",
        phone: businessData.phone || "",
        google_place_id: businessData.google_place_id || null,
        created_at: businessData.created_at || new Date().toISOString(),
        user_id: businessData.user_id,
        allow_shift_self_assignment: businessData.allow_shift_self_assignment || false,
        opening_hours: businessData.business_opening_hours?.map(oh => ({
          ...oh,
          business_id: businessData.id
        })) || null,
        last_attendance_sync_at: businessData.last_attendance_sync_at || null,
        dotypos_enabled: businessData.dotypos_enabled || false,
        dotypos_cloud_id: businessData.dotypos_cloud_id || null,
        payment_processing_enabled: businessData.payment_processing_enabled || false,
      };

      setSelectedBusinessDetails(fetchedDetails);
    } catch (error) {
      toast.error("Failed to load business details", { 
        description: (error as Error).message || "An unexpected error occurred." 
      });
      setSelectedBusinessDetails(null);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedBusinessId) {
      setSelectedBusinessDetails(null);
      setLoadingData(false);
      return;
    }

    fetchBusinessDetails(selectedBusinessId);
  }, [selectedBusinessId, fetchBusinessDetails]);

  const refreshBusinessDetails = useCallback(() => {
    if (selectedBusinessId) {
      fetchBusinessDetails(selectedBusinessId);
    }
  }, [selectedBusinessId, fetchBusinessDetails]);

  return {
    selectedBusinessDetails,
    setSelectedBusinessDetails,
    loadingData,
    refreshBusinessDetails,
  };
}